import { useEffect, useState } from "react";
import { authFetch } from "@/lib/auth-fetch";
import { apiUrl } from "@/lib/api-base";

export interface AiWeeklyNarrative {
  headline: string;
  body: string;
  pattern: string;
  suggestion: string;
}

/**
 * Personalized narrative from the backend, generated from what was actually written
 * in the given week (not just counts) — see GET /journals/insights/weekly. Returns
 * null while loading, if there's nothing to summarize, or on any failure; callers
 * should treat null as "nothing to show yet" and never surface an error state for it.
 */
export type WeeklyNarrativeStatus = "ok" | "insufficient_data" | "unavailable" | "loading";

/** Contract: GET /journals/insights/weekly?weekStart=YYYY-MM-DD */
interface WeeklyInsightResponse {
  narrative: AiWeeklyNarrative | null;
  /** Additive — older deploys omit it, so `narrative` stays the source of truth. */
  status?: "ok" | "insufficient_data" | "unavailable";
  entryCount?: number;
  minimumEntries?: number;
}

export function useAiWeeklyNarrative(
  weekStart: string,
  options?: { enabled?: boolean },
): { narrative: AiWeeklyNarrative | null; status: WeeklyNarrativeStatus; minimumEntries: number | null } {
  const enabled = options?.enabled ?? true;
  const [narrative, setNarrative] = useState<AiWeeklyNarrative | null>(null);
  const [status, setStatus] = useState<WeeklyNarrativeStatus>("loading");
  const [minimumEntries, setMinimumEntries] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setNarrative(null);
    setMinimumEntries(null);
    setStatus("loading");
    // Skip the request entirely when the card isn't showing the weekly view.
    if (!enabled) return;
    authFetch(apiUrl(`/journals/insights/weekly?weekStart=${weekStart}`))
      .then((res) => (res.ok ? res.json() : null))
      .then((data: WeeklyInsightResponse | null) => {
        if (cancelled) return;
        if (!data) {
          setStatus("unavailable");
          return;
        }
        if (typeof data.minimumEntries === "number") setMinimumEntries(data.minimumEntries);
        if (data.narrative) setNarrative(data.narrative);
        // `status` is additive: trust it when present, otherwise infer from the payload.
        setStatus(data.status ?? (data.narrative ? "ok" : "insufficient_data"));
      })
      .catch(() => {
        if (!cancelled) setStatus("unavailable");
      });
    return () => {
      cancelled = true;
    };
  }, [weekStart, enabled]);

  return { narrative, status, minimumEntries };
}
